import React, { useRef, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fieldLabelSx, nativeInputSx } from '../common/formFieldStyles';
import { formatCurrency } from '../../utils/currencyUtils';
import { stockUnitSuffix, toBaseUnitCost } from '../../utils/stockQuantityUtils';
import { BRAND } from '../../theme/brandTokens';

const quantitiesMatch = (lines, defaults) => (
  lines.length === defaults.length
  && lines.every((line) => {
    const original = defaults.find((item) => item.productId === line.productId);
    return original && Number(original.quantity) === Number(line.quantity);
  })
);

const AppointmentProductUsageSection = ({
  lines = [],
  onChange,
  currency,
  canSeeCosts = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const defaultsRef = useRef(null);
  if (!defaultsRef.current) {
    defaultsRef.current = (lines || []).map((line) => ({
      productId: line.productId,
      quantity: Number(line.quantity),
    }));
  }
  const defaults = defaultsRef.current;

  if (!lines.length) {
    return null;
  }

  const updateQuantity = (productId, quantity) => {
    const next = lines.map((line) => (
      line.productId === productId ? { ...line, quantity } : line
    ));
    onChange(next, !quantitiesMatch(next, defaults));
  };

  return (
    <Box id="appointment-product-usage" sx={{ borderTop: `1px solid ${BRAND.line}`, pt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {t('stock.usage.title')}
        </Typography>
        <IconButton
          id="appointment-product-usage-toggle"
          aria-label={t('stock.usage.toggle')}
          onClick={() => setOpen((current) => !current)}
          sx={{ color: BRAND.accentText }}
        >
          <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none' }} />
        </IconButton>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('stock.usage.help')}
      </Typography>
      <Collapse in={open}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {lines.map((line) => {
            const suffix = stockUnitSuffix(line.stockUnit);
            return (
              <Box key={line.productId}>
                <Typography variant="subtitle2" sx={fieldLabelSx}>
                  {line.name}
                  {suffix ? ` (${suffix})` : ''}
                </Typography>
                <Box
                  component="input"
                  id={`appointment-usage-${line.productId}`}
                  type="number"
                  min={0}
                  step="0.001"
                  aria-label={line.name}
                  value={line.quantity}
                  onChange={(event) => updateQuantity(line.productId, event.target.value)}
                  sx={nativeInputSx(false)}
                />
                {canSeeCosts && line.unitCost != null ? (
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(
                      Number(line.quantity) * Number(toBaseUnitCost(line.unitCost, line.contentQuantity)),
                      currency,
                    )}
                  </Typography>
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AppointmentProductUsageSection;
